import { formatLocale } from "../i18n/negotiate.ts";
import { translateOutside } from "../i18n/problems.ts";

/** The dashboard's language for Intl (the provider sets it on <html>), else the browser's. */
export function intlLocale(): string | undefined {
  const lang = typeof document !== "undefined" ? document.documentElement.lang : "";
  return lang ? formatLocale(lang) : undefined;
}

export function money(value: number | null | undefined, currency = "USD", digits = 0): string {
  if (value == null) return "—";
  try {
    return new Intl.NumberFormat(intlLocale(), { style: "currency", currency, maximumFractionDigits: digits, minimumFractionDigits: digits }).format(value);
  } catch {
    return `${value.toFixed(digits)} ${currency}`;
  }
}

export function cents(value: number | null | undefined, currency = "USD"): string {
  return value == null ? "—" : money(value / 100, currency, 2);
}

const numberFormats = new Map<string, Intl.NumberFormat>();
export function num(value: number | null | undefined, digits = 0): string {
  if (value == null) return "—";
  // one formatter per locale and precision: making an Intl.NumberFormat costs far more than formatting (live numbers
  // redraw often)
  const locale = intlLocale();
  const key = `${locale ?? ""}|${digits}`;
  let f = numberFormats.get(key);
  if (!f) numberFormats.set(key, (f = new Intl.NumberFormat(locale, { maximumFractionDigits: digits, minimumFractionDigits: digits })));
  return f.format(value);
}

export function date(value: string | null | undefined, withTime = false): string {
  if (!value) return "—";
  const d = new Date(value);
  return withTime
    ? d.toLocaleString(intlLocale(), { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString(intlLocale(), { year: "numeric", month: "short", day: "numeric" });
}

/** "now", "5 min. ago", "2 hr. ago", "3 days ago" (Intl, in `locale`), then the date. */
export function timeAgo(value: string, now: Date = new Date(), locale: string | undefined = intlLocale()): string {
  const seconds = Math.max(0, (now.getTime() - new Date(value).getTime()) / 1000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto", style: "short" });
  if (seconds < 60) return rtf.format(0, "second");
  if (seconds < 3600) return rtf.format(-Math.floor(seconds / 60), "minute");
  if (seconds < 86400) return rtf.format(-Math.floor(seconds / 3600), "hour");
  if (seconds < 86400 * 30) return rtf.format(-Math.floor(seconds / 86400), "day");
  return new Date(value).toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" });
}

export function relative(value: string | null | undefined): string {
  if (!value) return translateOutside("common.time.never") ?? "never";
  return timeAgo(value);
}

/** A design version's number as people see it: the API's `version_offset` is 1 when version 1 is the empty
 * placeholder made with the design, so its first build reads "version 1". URLs keep the stored number. */
export function shownVersion(number: number, offset: number | null | undefined): number {
  return Math.max(1, number - (offset ?? 0));
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join("") || "?";
}

export function titleCase(value: string): string {
  return value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
